import moment from "moment-timezone";

/**
 * "All banks" for bank-level reports.
 *
 * LiftCloud's bank reports take exactly one `bank_id`, so an all-banks report is
 * run once per bank in scope and the answers are merged here. Two merge shapes
 * exist, and a report declares which one it needs:
 *
 * - `rows`: a table. Every row gains a leading `bank` column (unless the
 *   report already names the bank, as `bank_name`). Sorting and
 *   pagination move to the client, because each per-bank request is sent
 *   without them (see `perBankBody`) and the server then returns every row.
 * - `perLift`: a chart keyed by lift. `lift_name` is prefixed with the bank's
 *   name so "Car 1" of two banks stays two series.
 *
 * Charts that plot a bank-wide aggregate (averages, percentages, up/down
 * splits) have no correct client-side merge and do not offer "All banks".
 */

/** The `filters.bankId` value that means "every bank in scope". */
export const ALL_BANKS_ID = "ALL_BANKS";

export type AllBanksMergeMode = "rows" | "perLift";

/** One bank in the all-banks selection (`filters.banks`). */
export interface AllBanksBank {
  id: string;
  name: string;
}

/** A per-bank report answer, as the server returned it. */
export interface BankReportResponse {
  bank: AllBanksBank;
  response: any;
}

export const isAllBanks = (bankId: string | undefined | null): boolean => bankId === ALL_BANKS_ID;

/**
 * The body sent for one bank: the page's body with that bank's id. In `rows`
 * mode pagination and sorting are blanked so the server returns every row
 * (`ValidateOrderingPaginateValues` is false for empty sort and zero paging).
 */
export const perBankBody = (body: any, bankId: string, mode: AllBanksMergeMode): any => {
  const out: any = { ...body, bank_id: bankId };
  if (mode === "rows") {
    out.page = 0;
    out.items_per_page = 0;
    out.sort_by = "";
    out.sort_direction = "";
  }
  return out;
};

/** The row array of a report answer: tables use `data` or `results`, charts `results`. */
export const responseRows = (response: any): any[] => {
  if (Array.isArray(response?.results)) return response.results;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const REPORT_DATE_FORMAT = "ddd. MMM DD. YYYY - HH:mm:ss";

/** A sortable key: numbers and report dates compare by value, everything else as text. */
const sortKey = (value: any): number | string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "number") return value;
  const text = String(value);
  if (text.trim() !== "" && !Number.isNaN(Number(text))) return Number(text);
  const date = moment(text, REPORT_DATE_FORMAT, true);
  if (date.isValid()) return date.valueOf();
  return text.toLowerCase();
};

const compareValues = (a: any, b: any): number => {
  const ka = sortKey(a);
  const kb = sortKey(b);
  if (typeof ka === "number" && typeof kb === "number") return ka - kb;
  return String(ka).localeCompare(String(kb));
};

/**
 * Sorts and pages merged rows the way LiftCloud's `SortAndPaginate` does for one
 * bank: no sort unless both field and direction are set, and no paging unless
 * both page and page size are positive.
 */
export const sortAndPage = (
  rows: any[],
  sortBy: string | undefined,
  sortDirection: string | undefined,
  page: number | undefined,
  itemsPerPage: number | undefined,
): any[] => {
  let out = [...rows];
  if (sortBy && sortDirection) {
    const sign = sortDirection.toLowerCase() === "asc" ? 1 : -1;
    out = out
      .map((row, index) => ({ row, index }))
      .sort((x, y) => sign * compareValues(x.row[sortBy], y.row[sortBy]) || x.index - y.index)
      .map(({ row }) => row);
  }
  if (page && page > 0 && itemsPerPage && itemsPerPage > 0) {
    const start = (page - 1) * itemsPerPage;
    out = out.slice(start, start + itemsPerPage);
  }
  return out;
};

/**
 * Merges per-bank answers into one answer of the same shape the page already
 * renders: `{ results, total }`.
 */
export const mergeBankResponses = (
  answers: BankReportResponse[],
  mode: AllBanksMergeMode,
  body: any,
): { results: any[]; total: number } => {
  const merged: any[] = [];
  answers.forEach(({ bank, response }) => {
    responseRows(response).forEach((row: any) => {
      if (mode === "rows") {
        const named = row && typeof row === "object" && "bank_name" in row;
        merged.push(named ? row : { bank: bank.name, ...row });
      } else if (row && typeof row === "object" && typeof row.lift_name === "string") {
        merged.push({ ...row, lift_name: `${bank.name} / ${row.lift_name}` });
      } else {
        merged.push(row);
      }
    });
  });
  if (mode === "perLift") return { results: merged, total: merged.length };
  const sorted = sortAndPage(
    merged,
    body?.sort_by,
    body?.sort_direction,
    body?.page,
    body?.items_per_page,
  );
  return { results: sorted, total: merged.length };
};

const csvCell = (text: string): string => `"${text.replaceAll('"', '""')}"`;

const hasBankColumn = (headerLine: string): boolean =>
  headerLine.split(",").some((cell) => cell.trim().replace(/^"|"$/g, "").toUpperCase() === "BANK");

/**
 * Joins per-bank CSV downloads into one file with a leading BANK column, or
 * as-is when the report's own header already has a BANK column. The header is
 * taken from the first bank that has one; blank lines are dropped.
 */
export const mergeBankCsv = (parts: { bank: AllBanksBank; csv: string }[]): string => {
  let header: string | undefined;
  let prefix = true;
  const lines: string[] = [];
  parts.forEach(({ bank, csv }) => {
    const rows = csv.split(/\r?\n/).filter((line) => line.trim() !== "");
    if (rows.length === 0) return;
    if (header === undefined) {
      prefix = !hasBankColumn(rows[0]);
      header = prefix ? `BANK,${rows[0]}` : rows[0];
    }
    rows.slice(1).forEach((line) => lines.push(prefix ? `${csvCell(bank.name)},${line}` : line));
  });
  if (header === undefined) return "";
  return [header, ...lines].join("\n");
};

/** Runs `task` over `items` with at most `limit` in flight, keeping input order. */
export const mapWithConcurrency = async <T, R>(
  items: T[],
  limit: number,
  task: (item: T) => Promise<R>,
): Promise<R[]> => {
  const results: R[] = new Array(items.length);
  let next = 0;
  const worker = async (): Promise<void> => {
    if (next >= items.length) return;
    const index = next;
    next += 1;
    results[index] = await task(items[index]);
    await worker();
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
  return results;
};
